import { Alert, Box, Button, Divider, FileInput, Image, Stack } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconTrash, IconUpload } from '@tabler/icons-react';
import { useState } from 'react';
import {
  useDeleteSignature,
  useSignatureDownloadUrl,
  useUploadSignature,
} from '../hooks/useSignature';
import TabHeader from './TabHeader';

const MAX_FILE_SIZE_MB = 2;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/jpg'];

type Props = {
  hasSignature: boolean;
};

export default function SignatureUpload({ hasSignature }: Props) {
  const { downloadUrl, refetch: refetchDownloadUrl } = useSignatureDownloadUrl(hasSignature);
  const { uploadSignature, isLoading: isUploading } = useUploadSignature();
  const { deleteSignature, isLoading: isDeleting } = useDeleteSignature();
  const [file, setFile] = useState<File | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const validateFile = (file: File | null): string | null => {
    if (!file) return null;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      return 'File must be a PNG or JPEG image.';
    }

    if (file.size > MAX_FILE_SIZE_BYTES) {
      const sizeMB = (file.size / (1024 * 1024)).toFixed(2);
      return `File size (${sizeMB}MB) exceeds maximum allowed size of ${MAX_FILE_SIZE_MB}MB.`;
    }

    return null;
  };

  const handleFileChange = (selectedFile: File | null) => {
    const error = validateFile(selectedFile);
    setValidationError(error);
    setFile(error ? null : selectedFile);

    if (error) {
      notifications.show({
        title: 'Invalid file',
        message: error,
        color: 'red',
        icon: <IconAlertCircle size={16} />,
      });
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    const error = validateFile(file);
    if (error) {
      setValidationError(error);
      return;
    }

    try {
      await uploadSignature(file);
      setFile(null);
      setValidationError(null);
      await refetchDownloadUrl();
      notifications.show({
        title: 'Signature uploaded',
        message: 'Your signature has been uploaded successfully.',
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Upload failed',
        message: 'An error occurred while uploading your signature.',
        color: 'red',
      });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteSignature();
      notifications.show({
        title: 'Signature removed',
        message: 'Your signature has been removed.',
        color: 'green',
      });
    } catch {
      notifications.show({
        title: 'Delete failed',
        message: 'An error occurred while removing your signature.',
        color: 'red',
      });
    }
  };

  return (
    <Stack>
      <TabHeader
        title="Signature"
        subtitle="Upload an image of your signature. This will be used in suitability reports and other documents."
      />

      <Divider size="xs" color="gray.2" mt="xs" />

      {hasSignature && downloadUrl && (
        <Box>
          <Image src={downloadUrl} alt="Your signature" maw={300} radius="md" mb="xs" />
          <Button
            variant="subtle"
            color="red"
            size="xs"
            leftSection={<IconTrash size={14} />}
            onClick={handleDelete}
            loading={isDeleting}
          >
            Remove signature
          </Button>
        </Box>
      )}

      <FileInput
        label="Signature Image"
        description="PNG or JPEG format, max 2MB"
        placeholder="Click to select file"
        accept="image/png,image/jpeg,image/jpg"
        value={file}
        onChange={handleFileChange}
        leftSection={<IconUpload size={16} />}
        maw={400}
        error={validationError}
      />

      {validationError && (
        <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
          {validationError}
        </Alert>
      )}

      <Button
        onClick={handleUpload}
        loading={isUploading}
        disabled={!file || !!validationError}
        leftSection={<IconUpload size={16} />}
        w="fit-content"
      >
        Upload Signature
      </Button>
    </Stack>
  );
}
